// src/utils/marketHeat.ts
import {
  FearGreedIndex,
  MarketCapData,
  GoogleTrendsData,
  RSIData,
  BeaconchainData,
  AggregatedOpenInterestData,
  NewsData,
  AggregatedWalletData,
  MarketHeatScore
} from '../types';

import {
  MARKET_HEAT_WEIGHTS,
  THRESHOLDS,
  TARGET_MARKET_CAP,
  // Remove unused import to fix linting warning
  // CURRENT_MARKET_CAP_MILESTONE
} from '../constants';

/**
 * Normalizes a value to a 0-100 scale based on min and max values
 */
const normalizeValue = (value: number, min: number, max: number): number => {
  if (value <= min) return 0;
  if (value >= max) return 100;
  return ((value - min) / (max - min)) * 100;
};

/**
 * Calculates a score for the Fear and Greed Index
 * Higher fear and greed index = higher market heat
 */
const calculateFearGreedScore = (fearGreedIndex: FearGreedIndex | null): number => {
  if (!fearGreedIndex) return 50; // Neutral if no data
  
  // Fear and Greed Index is already on a 0-100 scale
  return fearGreedIndex.value;
};

/**
 * Calculates a score based on how close we are to the target market cap
 * Closer to target = higher market heat
 */
const calculateMarketCapProximityScore = (marketCap: MarketCapData | null): number => {
  if (!marketCap) return 50; // Neutral if no data
  
  // Calculate how close we are to the target as a percentage
  const percentToTarget = (marketCap.totalMarketCap / TARGET_MARKET_CAP) * 100;
  
  // Normalize to 0-100 scale
  // 0% of target = 0 score, 100% or more of target = 100 score
  return Math.min(percentToTarget, 100);
};

/**
 * Calculates a score based on Google Trends data
 * Higher search interest = higher market heat
 */
const calculateGoogleTrendsScore = (googleTrends: GoogleTrendsData | null): number => {
  if (!googleTrends) return 50; // Neutral if no data
  
  // Google Trends data is already normalized to 0-100
  return googleTrends.relativeValue;
};

/**
 * Calculates a score based on Ethereum RSI
 * Higher RSI = higher market heat
 */
const calculateRSIScore = (rsiData: RSIData | null): number => {
  if (!rsiData) return 50; // Neutral if no data
  
  // RSI is already on a 0-100 scale
  return rsiData.value;
};

/**
 * Calculates a score based on the Beaconchain unstaking queue
 * Higher queue = higher market heat (more people unstaking to sell/take profits)
 * Lower queue = lower market heat (more people keeping ETH staked)
 */
const calculateUnstakingQueueScore = (beaconchain: BeaconchainData | null): number => {
  if (!beaconchain) return 50; // Neutral if no data
  
  // Define reasonable min and max queue lengths
  // Min: 0 validators (no one unstaking) = 0 score
  // Max: 100,000 validators (mass exodus) = 100 score
  const minQueue = 0;
  const maxQueue = 100000;
  
  // Higher unstaking queue = higher market heat (top signal)
  return normalizeValue(beaconchain.unstakingQueueLength, minQueue, maxQueue);
};

/**
 * Calculates a score based on open interest in futures markets
 * Higher open interest = higher market heat
 */
const calculateOpenInterestScore = (
  ethOpenInterest: AggregatedOpenInterestData | null,
  spx6900OpenInterest: AggregatedOpenInterestData | null
): number => {
  if (!ethOpenInterest && !spx6900OpenInterest) return 50; // Neutral if no data
  
  // Define baseline and maximum expected open interest in USD
  const baselineOpenInterestETH = 1_000_000_000; // $1B
  const maxOpenInterestETH = 10_000_000_000; // $10B
  
  const baselineOpenInterestSPX = 100_000_000; // $100M
  const maxOpenInterestSPX = 1_000_000_000; // $1B
  
  // Calculate normalized scores for each asset
  let ethScore = 50;
  let spxScore = 50;
  
  if (ethOpenInterest) {
    ethScore = normalizeValue(
      ethOpenInterest.totalValueUSD,
      baselineOpenInterestETH,
      maxOpenInterestETH
    );
  }
  
  if (spx6900OpenInterest) {
    spxScore = normalizeValue(
      spx6900OpenInterest.totalValueUSD,
      baselineOpenInterestSPX,
      maxOpenInterestSPX
    );
  }
  
  // Weight ETH more heavily than SPX6900 (70/30 split)
  return ethOpenInterest && spx6900OpenInterest
    ? (ethScore * 0.7) + (spxScore * 0.3)
    : ethOpenInterest
      ? ethScore
      : spxScore;
};

/**
 * Calculates a score based on SPX6900 news mentions
 * Binary scoring: 0 if no mentions, 80 if any mentions found
 */
const calculateNewsActivityScore = (newsData: NewsData | null): number => {
  if (!newsData) return 0; // No data = no mentions
  
  // Check if SPX6900 is mentioned in any news source
  const mentionCount = newsData.mentionsCount['SPX6900'] || 0;
  
  // Binary scoring: 0 for no mentions, 80 for any mentions
  return mentionCount > 0 ? 80 : 0;
};

/**
 * Calculates a score based on wallet activity
 * Simplified scoring based on number of selling wallets:
 * 0 sellers = 0 score, 1 seller = 80 score, 2 sellers = 90 score, 3+ sellers = 100 score
 */
const calculateWalletActivityScore = (walletData: AggregatedWalletData | null): number => {
  if (!walletData) return 50; // Neutral if no data
  
  const sellingWallets = walletData.sellingWallets;
  
  // Simple mapping based on number of sellers
  if (sellingWallets === 0) return 0;   // No sellers = no heat
  if (sellingWallets === 1) return 80;  // 1 seller = high heat
  if (sellingWallets === 2) return 90;  // 2 sellers = very high heat
  return 100;                           // 3+ sellers = maximum heat
};

/**
 * Calculates sell pressure score based on distribution
 */
const calculateSellPressureScore = (
  sellPressureDistribution: Record<'none' | 'low' | 'medium' | 'high', number>,
  totalWallets: number
): number => {
  if (totalWallets === 0) return 50;
  
  const noneRatio = sellPressureDistribution.none / totalWallets;
  const lowRatio = sellPressureDistribution.low / totalWallets;
  const mediumRatio = sellPressureDistribution.medium / totalWallets;
  const highRatio = sellPressureDistribution.high / totalWallets;
  
  // Weighted score: none = 100, low = 70, medium = 40, high = 10
  return (noneRatio * 100) + (lowRatio * 70) + (mediumRatio * 40) + (highRatio * 10);
};

/**
 * Interprets the market heat score
 */
const interpretMarketHeat = (score: number): 'Cold' | 'Cool' | 'Neutral' | 'Warm' | 'Hot' | 'Overheated' => {
  if (score <= THRESHOLDS.MARKET_HEAT.COLD) return 'Cold';
  if (score <= THRESHOLDS.MARKET_HEAT.COOL) return 'Cool';
  if (score <= THRESHOLDS.MARKET_HEAT.NEUTRAL) return 'Neutral';
  if (score <= THRESHOLDS.MARKET_HEAT.WARM) return 'Warm';
  if (score <= THRESHOLDS.MARKET_HEAT.HOT) return 'Hot';
  return 'Overheated';
};

/**
 * Calculates the overall market heat score based on all available metrics
 */
export const calculateMarketHeatScore = (
  fearGreedIndex: FearGreedIndex | null,
  marketCap: MarketCapData | null,
  googleTrends: GoogleTrendsData | null,
  ethereumRSI: RSIData | null,
  beaconchain: BeaconchainData | null,
  ethOpenInterest: AggregatedOpenInterestData | null,
  spx6900OpenInterest: AggregatedOpenInterestData | null,
  newsData: NewsData | null,
  walletData: AggregatedWalletData | null
): MarketHeatScore => {
  // Calculate individual component scores
  const fearGreedScore = calculateFearGreedScore(fearGreedIndex);
  const marketCapScore = calculateMarketCapProximityScore(marketCap);
  const googleTrendsScore = calculateGoogleTrendsScore(googleTrends);
  const rsiScore = calculateRSIScore(ethereumRSI);
  const unstakingQueueScore = calculateUnstakingQueueScore(beaconchain);
  const openInterestScore = calculateOpenInterestScore(ethOpenInterest, spx6900OpenInterest);
  const newsActivityScore = calculateNewsActivityScore(newsData);
  const walletActivityScore = calculateWalletActivityScore(walletData);
  
  // Apply weights to each component (excluding open interest)
  const weightedScores = {
    fearGreedIndex: fearGreedScore * MARKET_HEAT_WEIGHTS.FEAR_GREED_INDEX,
    marketCapProximity: marketCapScore * MARKET_HEAT_WEIGHTS.MARKET_CAP_PROXIMITY,
    googleTrends: googleTrendsScore * MARKET_HEAT_WEIGHTS.GOOGLE_TRENDS,
    rsi: rsiScore * MARKET_HEAT_WEIGHTS.RSI,
    unstakingQueue: unstakingQueueScore * MARKET_HEAT_WEIGHTS.UNSTAKING_QUEUE,
    newsActivity: newsActivityScore * MARKET_HEAT_WEIGHTS.NEWS_ACTIVITY,
    walletActivity: walletActivityScore * MARKET_HEAT_WEIGHTS.WALLET_ACTIVITY
  };
  
  // Calculate overall score
  const overallScore = Object.values(weightedScores).reduce((sum, score) => sum + score, 0);
  
  // Round to 1 decimal place
  const roundedScore = Math.round(overallScore * 10) / 10;
  
  // Interpret the score
  const interpretation = interpretMarketHeat(roundedScore);
  
  return {
    overall: roundedScore,
    components: {
      fearGreedIndex: fearGreedScore,
      marketCapProximity: marketCapScore,
      googleTrends: googleTrendsScore,
      rsi: rsiScore,
      unstakingQueue: unstakingQueueScore,
      newsActivity: newsActivityScore,
      walletActivity: walletActivityScore
    },
    interpretation,
    timestamp: Date.now()
  };
};

/**
 * Calculates a simplified market heat score based on available metrics
 * Useful when not all data sources are available
 */
export const calculateSimplifiedMarketHeatScore = (
  fearGreedIndex: FearGreedIndex | null,
  ethereumRSI: RSIData | null,
  marketCap: MarketCapData | null
): number => {
  let score = 50; // Start with neutral
  let weightSum = 0;
  
  if (fearGreedIndex) {
    score += fearGreedIndex.value * MARKET_HEAT_WEIGHTS.FEAR_GREED_INDEX;
    weightSum += MARKET_HEAT_WEIGHTS.FEAR_GREED_INDEX;
  }
  
  if (ethereumRSI) {
    score += ethereumRSI.value * MARKET_HEAT_WEIGHTS.RSI;
    weightSum += MARKET_HEAT_WEIGHTS.RSI;
  }
  
  if (marketCap) {
    const marketCapScore = calculateMarketCapProximityScore(marketCap);
    score += marketCapScore * MARKET_HEAT_WEIGHTS.MARKET_CAP_PROXIMITY;
    weightSum += MARKET_HEAT_WEIGHTS.MARKET_CAP_PROXIMITY;
  }
  
  // Normalize based on available weights
  return weightSum > 0 ? score / weightSum : 50;
};

/**
 * Checks if the market heat score indicates a potential market top
 */
export const isPotentialMarketTop = (score: number): boolean => {
  return score >= THRESHOLDS.MARKET_HEAT.HOT;
};

/**
 * Generates a summary of the market heat score
 */
export const generateMarketHeatSummary = (heatScore: MarketHeatScore): string => {
  const { overall, components, interpretation } = heatScore;
  
  // Find the highest contributing factors
  const sortedComponents = Object.entries(components)
    .map(([key, value]) => ({ key, value }))
    .sort((a, b) => b.value - a.value);
  
  const topFactors = sortedComponents.slice(0, 3).map(item => item.key);
  
  let summary = `Market Heat Score: ${overall}/100 (${interpretation})\n`;
  summary += `Top contributing factors: ${topFactors.join(', ')}\n`;
  
  if (overall >= THRESHOLDS.MARKET_HEAT.HOT) {
    summary += "WARNING: Market showing signs of overheating. Consider reducing exposure.";
  } else if (overall <= THRESHOLDS.MARKET_HEAT.COLD) {
    summary += "Market sentiment is extremely bearish. Potential buying opportunity.";
  }
  
  return summary;
};

// Create a named object for export to fix the linting warning
const marketHeatUtils = {
  calculateMarketHeatScore,
  calculateSimplifiedMarketHeatScore,
  isPotentialMarketTop,
  generateMarketHeatSummary
};

export default marketHeatUtils;
